/*
 * ScreenSwiper
 * @module components
 * @flow weak
 */

import React from 'react';
import {
  compose,
  lifecycle,
  pure,
  withHandlers,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from 'recompose';
import { withRouter } from 'react-router-dom';
import * as R from 'ramda';

import { withDevice } from '~/features/App/Device';
import { AceService } from '~/services';
import { findRouteIndexByPathname } from '~/helpers';

import { ScreenSwiper as SCS } from './ScreenSwiper';

const MIN_PIXELS_FOR_SWIPING = 120;

// NOTE: to stop swiping on target add to html element class "screen-swiper-disabled"
const checkIfSwipingDenied = target => {
  if (!target.parentNode) {
    return false;
  }

  return (
    target.classList.contains('screen-swiper-disabled') ||
    checkIfSwipingDenied(target.parentNode)
  );
};

const prepareScreens = routes => {
  const screens = [];

  R.pipe(
    R.values,
    R.forEach(({ link, route, routes }) => {
      if (routes) {
        screens.push(...prepareScreens(routes));
      } else {
        screens.push({ link, route });
      }
    }),
  )(routes);

  return screens;
};

const enhance = compose(
  withDevice,
  withRouter,
  withProps(({ routes }) => ({
    screens: prepareScreens(routes),
    swiperRef: React.createRef(),
  })),
  withStateHandlers(
    { redirect: null },
    {
      setRedirect: state => redirect => ({
        ...state,
        redirect,
      }),
    },
  ),
  withPropsOnChange(['location'], ({ redirect, setRedirect }) => {
    if (redirect) {
      setRedirect(null);
    }

    return {};
  }),
  withHandlers({
    swipeLeft: ({ location: { pathname }, screens, setRedirect }) => () => {
      const currentIndex = findRouteIndexByPathname(pathname, screens);
      const previousIndex =
        currentIndex <= 0 ? screens.length - 1 : currentIndex - 1;

      setRedirect(screens[previousIndex].link);
    },
    swipeRight: ({ location: { pathname }, screens, setRedirect }) => () => {
      const currentIndex = findRouteIndexByPathname(pathname, screens);
      const nextIndex =
        currentIndex >= screens.length - 1 ? 0 : currentIndex + 1;

      setRedirect(screens[nextIndex].link);
    },
  }),
  withHandlers({
    onSwipe: ({
      orientation,
      swiperRef,
      swipeLeft,
      swipeRight,
    }) => startEvent => {
      const { pageX: startX, pageY: startY } = startEvent.changedTouches[0];
      const { current } = swiperRef;
      let isTouchEnded = false;

      current.ontouchend = async endEvent => {
        current.ontouchend = null;

        if (
          startEvent.touches.length > 1 ||
          checkIfSwipingDenied(startEvent.target)
        ) {
          return;
        }

        const elevateTokens = await AceService.getElevateTokens();

        // workaround to prevent screens swiping before transaction complete
        if (elevateTokens) {
          return;
        }

        const { pageX: endX, pageY: endY } = endEvent.changedTouches[0];
        const isPortrait = orientation === 'portrait';
        const swipedPixels = isPortrait
          ? Math.abs(startX - endX)
          : Math.abs(startY - endY);
        const isSwiped = isPortrait ? startX !== endX : startY !== endY;

        if (
          !isSwiped ||
          swipedPixels < MIN_PIXELS_FOR_SWIPING ||
          isTouchEnded
        ) {
          return;
        }

        const isSwipedLeft = isPortrait ? startX < endX : startY < endY;

        isTouchEnded = true;

        if (isSwipedLeft) {
          swipeLeft();
        } else {
          swipeRight();
        }
      };
    },
  }),
  lifecycle({
    componentDidMount() {
      const { swiperRef, onSwipe } = this.props;

      swiperRef.current.addEventListener('touchstart', onSwipe, {
        passive: true,
      });
    },
    componentWillUnmount() {
      const { swiperRef, onSwipe } = this.props;

      swiperRef.current.removeEventListener('touchstart', onSwipe);
    },
  }),
  pure,
);

export const ScreenSwiper = enhance(SCS);
